import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../../supabase";
import styles from "./AdminPTLPreview.module.css";
import Modal from "../../components/admin/FormModal";
import formStyles from "../../components/admin/FormModal.module.css"; 
import Input from "../../components/admin/Input";
import AdminList from "../../components/admin/AdminList";
import Layout from "../../components/admin/Layout";
import IconTextButton from "../../components/admin/IconTextButton";
import { Helmet } from "react-helmet";
// 상수 정의
const INITIAL_FORM_DATA = {
  date: "",
  time: "",
  stadium: "",
  team1: "",
  team2: "",
  team3: null,
  commentary_a: null,
  commentary_b: null,
  commentary_c: null,
  team1_trophy_avg: null,
  team2_trophy_avg: null,
  team3_trophy_avg: null,
  team1_wdl: null,
  team2_wdl: null,
  team3_wdl: null,
  vs1_home_avg: null,
  vs1_away_avg: null,
  vs2_home_avg: null,
  vs2_away_avg: null,
  vs3_home_avg: null,
  vs3_away_avg: null,
};

const TABLE_HEADERS = [
  { label: "ID", key: "id" },
  { label: "경기 일정", key: "datetime" },
  { label: "경기장", key: "stadium" },
  { label: "팀1", key: "team1" },
  { label: "팀2", key: "team2" },
  { label: "팀3", key: "team3" },
];

const formatDateTime = (date, time) => {
  if (!date || !time) return '';
  
  // 날짜 포맷팅 (예: 2024-01-01 → 2024.01.01)
  const formattedDate = date.replace(/-/g, '.');
  
  // 시간 포맷팅 (예: 14:00:00 → 14:00)
  const formattedTime = time.substring(0, 5); // HH:mm 형식만 추출
  
  return `${formattedDate} ${formattedTime}`;
};

function AdminPTLPreview() {
  // 상태 관리
  const [previews, setPreviews] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPreview, setEditingPreview] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 데이터 페칭
  const fetchPreviews = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
  
      const { data: previewsData, error: previewsError } = await supabase
        .from("ptl_preview")
        .select(`
          id, date, time, stadium, 
          team1 ( id, name, win_draw_lose ),
          team2 ( id, name, win_draw_lose ),
          team3 ( id, name, win_draw_lose ),
          commentary_a,
          commentary_b,
          commentary_c,
          team1_trophy_avg,
          team2_trophy_avg,
          team3_trophy_avg,
          team1_wdl,
          team2_wdl,
          team3_wdl,
          vs1_home_avg,
          vs1_away_avg,
          vs2_home_avg,
          vs2_away_avg,
          vs3_home_avg,
          vs3_away_avg
        `);
  
      if (previewsError) throw previewsError;
  
      const sortedData = previewsData
      .map((preview) => ({
        ...preview,
        team1: { id: preview.team1?.id || null, name: preview.team1?.name || "" },
        team2: { id: preview.team2?.id || null, name: preview.team2?.name || "" },
        team3: preview.team3
          ? { id: preview.team3.id, name: preview.team3.name }
          : { id: null, name: "" }, // team3 데이터가 없는 경우 처리
      }))
      .sort((a, b) => b.id - a.id);

    setPreviews(sortedData);
  } catch (err) {
    setError(err.message);
    console.error("Error fetching previews:", err);
  } finally {
    setIsLoading(false);
  }
  }, []);

  const fetchTeams = useCallback(async () => {
    try {
      const { data, error } = await supabase.from("team").select("id, name");
      if (error) throw error;
      setTeams(data);
    } catch (err) {
      console.error("Error fetching teams:", err);
    }
  }, []);

  useEffect(() => {
    fetchPreviews();
    fetchTeams();
  }, [fetchPreviews, fetchTeams]);

  // 폼 핸들링
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setEditingPreview(null);
  }, []);

  const validateForm = useCallback(() => {
    const requiredFields = ["date", "time", "stadium", "team1", "team2"];
    return requiredFields.every((field) => formData[field]); // team3는 검증 제외
  }, [formData]);

  // 모달 핸들링
  const openEditModal = useCallback((preview) => {
    setFormData({
      date: preview.date || "",
      time: preview.time || "",
      stadium: preview.stadium || "",
      team1: teams.find((team) => team.name === preview.team1)?.id || "", // 이름으로 ID 매칭
      team2: teams.find((team) => team.name === preview.team2)?.id || "",
      team3: teams.find((team) => team.name === preview.team3)?.id || "",
      commentary_a: preview.commentary_a || "",
      commentary_b: preview.commentary_b || "",
      commentary_c: preview.commentary_c || "",
      team1_trophy_avg: preview.team1_trophy_avg || null,
      team2_trophy_avg: preview.team2_trophy_avg || null,
      team3_trophy_avg: preview.team3_trophy_avg || null,
      team1_wdl: preview.team1_wdl || null,
      team2_wdl: preview.team2_wdl || null,
      team3_wdl: preview.team3_wdl || null,
      vs1_home_avg: preview.vs1_home_avg || null,
      vs1_away_avg: preview.vs1_away_avg || null,
      vs2_home_avg: preview.vs2_home_avg || null,
      vs2_away_avg: preview.vs2_away_avg || null,
      vs3_home_avg: preview.vs3_home_avg || null,
      vs3_away_avg: preview.vs3_away_avg || null,
    });
    setEditingPreview(preview);
    setIsModalOpen(true);
  }, [teams]);

  // 데이터 제출
  const handleFormSubmit = async () => {
    try {
      if (!validateForm()) {
        alert("필수 항목을 모두 입력해주세요.");
        return;
      }
  
      setIsLoading(true);
  
      // `team3` 관련 필드 조건부로 추가
      const {
        date, time, stadium, team1, team2, team3,
        commentary_a, commentary_b, commentary_c,
        team1_trophy_avg, team2_trophy_avg, team3_trophy_avg,
        vs1_home_avg, vs1_away_avg, vs2_home_avg, vs2_away_avg,
        vs3_home_avg, vs3_away_avg,
        team1_wdl, team2_wdl, team3_wdl,
      } = formData;
  
      const updateData = {
        date,
        time,
        stadium,
        team1,
        team2,
        commentary_a,
        commentary_b,
        vs1_home_avg,
        vs1_away_avg,
        vs2_home_avg,
        vs2_away_avg,
        team1_wdl,
        team2_wdl,
        team3_wdl,
        team1_trophy_avg,
        team2_trophy_avg,
        ...(team3 && { team3 }),
        ...(team3 && { commentary_c }), // team3가 있을 때만 추가
        ...(team3 && { team3_trophy_avg }), // 관련 필드도 조건부로 추가
        ...(team3 && { vs3_home_avg, vs3_away_avg }), // 관련 필드도 조건부로 추가
      };
  
      if (editingPreview) {
        const { error } = await supabase
          .from("ptl_preview")
          .update(updateData)
          .eq("id", editingPreview.id);
  
        if (error) throw error;
        alert("프리뷰가 성공적으로 수정되었습니다.");
      } else {
        const { error } = await supabase
          .from("ptl_preview")
          .insert([updateData]);
  
        if (error) throw error;
        alert("프리뷰가 성공적으로 추가되었습니다.");
      }
  
      setIsModalOpen(false);
      resetForm();
      fetchPreviews();
    } catch (err) {
      console.error("Error submitting form:", err);
      alert("오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
    }
  };

  // 렌더링
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Layout>
      <Helmet>
        <title>어드민:프리뷰</title>
      </Helmet>
        {isLoading && <div className={styles.loading}>Loading...</div>}
        <div className={styles.toolBar}>
          <IconTextButton
            icon="icon_plus_square.svg"
            text="추가하기"
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            state="Enabled"
          />
        </div>
        <AdminList
  headers={TABLE_HEADERS}
  data={previews.map((preview) => ({
    ...preview,
    datetime: formatDateTime(preview.date, preview.time),
    team1: preview.team1.name,
    team2: preview.team2.name,
    team3: preview.team3?.name || "N/A",
  }))}
  onRowClick={openEditModal}
/>

        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          onSubmit={handleFormSubmit}
        >
          <Input
            label="날짜"
            type="date"
            name="date"
            value={formData.date}
            onChange={handleInputChange}
            required
          />
          <Input
            label="시간"
            type="time"
            name="time"
            value={formData.time}
            onChange={handleInputChange}
            required
          />
          <Input
            label="경기장"
            type="text"
            name="stadium"
            value={formData.stadium}
            onChange={handleInputChange}
            required
          />
          <Input
            label="팀1"
            type="select"
            name="team1"
            value={formData.team1}
            onChange={handleInputChange}
            options={teams}
            required
          />
          <Input
            label="팀1 코멘트"
            type="textarea"
            name="commentary_a"
            value={formData.commentary_a}
            onChange={handleInputChange}
            rows="3"
            required
          />
          <Input
            label="팀2"
            type="select"
            name="team2"
            value={formData.team2}
            onChange={handleInputChange}
            options={teams}
            required
          />
          <Input
            label="팀2 코멘트"
            type="textarea"
            name="commentary_b"
            value={formData.commentary_b}
            onChange={handleInputChange}
            rows="3"
            required
          />
          <Input
            label="팀3"
            type="select"
            name="team3"
            value={formData.team3}
            onChange={handleInputChange}
            options={[{ id: "", name: "No Team" }, ...teams]}
            required
          />
          <Input
            label="팀3 코멘트"
            type="textarea"
            name="commentary_c"
            value={formData.commentary_c}
            onChange={handleInputChange}
            rows="3"
            required
          />
          <div className={formStyles.input_container}>
            <Input
              label="팀1 우승 확률"
              type="number"
              name="team1_trophy_avg"
              value={formData.team1_trophy_avg}
              onChange={handleInputChange}
            />
            <Input
              label="팀2 우승 확률"
              type="number"
              name="team2_trophy_avg"
              value={formData.team2_trophy_avg}
              onChange={handleInputChange}
            />
            <Input
              label="팀3 우승 확률"
              type="number"
              name="team3_trophy_avg"
              value={formData.team3_trophy_avg}
              onChange={handleInputChange}
            />
          </div>
          <div className={formStyles.input_container}>
            <Input
              label="팀1 전적"
              type="text"
              name="team1_wdl"
              value={formData.team1_wdl}
              onChange={handleInputChange}
            />
            <Input
              label="팀2 전적"
              type="text"
              name="team2_wdl"
              value={formData.team2_wdl}
              onChange={handleInputChange}
            />
            <Input
              label="팀3 전적"
              type="text"
              name="team3_wdl"
              value={formData.team3_wdl}
              onChange={handleInputChange}
            />
          </div>
          <div className={formStyles.input_container}>
            <Input
              label="팀1vs팀2 홈팀 승률"
              type="number"
              name="vs1_home_avg"
              value={formData.vs1_home_avg}
              onChange={handleInputChange}
            />
            <Input
              label="팀1vs팀2 원정팀 승률"
            type="number"
            name="vs1_away_avg"
            value={formData.vs1_away_avg}
              onChange={handleInputChange}
            />
          </div>
          <div className={formStyles.input_container}>
            <Input
              label="팀2vs팀3 홈팀 승률"
            type="number"
            name="vs2_home_avg"
            value={formData.vs2_home_avg}
            onChange={handleInputChange}
          />
          <Input
            label="팀2vs팀3 원정팀 승률"
            type="number"
            name="vs2_away_avg"
            value={formData.vs2_away_avg}
            onChange={handleInputChange}
            />
          </div>
          <div className={formStyles.input_container}>
            <Input
              label="팀3vs팀1 홈팀 승률"
            type="number"
            name="vs3_home_avg"
            value={formData.vs3_home_avg}
            onChange={handleInputChange}
          />
          <Input
            label="팀3vs팀1 원정팀 승률"
            type="number"
            name="vs3_away_avg"
            value={formData.vs3_away_avg}
              onChange={handleInputChange}
            />
          </div>

        </Modal>
    </Layout>
  );
}

export default AdminPTLPreview;